import { find } from 'lodash';

const IFRAMEID = 'credit-card-add-iframe';

class CreditCardAddBaseController {
    constructor($element, $sce, $timeout, settings, digitalPayPaymentInstrumentsService, digitalPayIframeEventHandler) {
        this.$element = $element;
        this.$sce = $sce;
        this.$timeout = $timeout;
        this.settings = settings;
        this.digitalPayPaymentInstrumentsService = digitalPayPaymentInstrumentsService;
        this.iframeEventHandler = digitalPayIframeEventHandler;
    }

    $onInit() {
        this.requiresVerification = false; // WM requires verify=true to be sent as part of credit card tokenization
        this.defaultIsSavingCard = this.isSavingCard;
        this.isLoading = true;
        this.iframeId = IFRAMEID;
        this.isIframeLoadedSuccess = false;
        this.iframeUrl = undefined;
        this.iframeLoadTimeoutPromise = undefined;
        this.timeoutPeriodInMillseconds = this.settings.digitalPay.iframeTimeoutInMilliseconds;
        this.creditCardTypes = this.settings.digitalPayCreditCardNetworks;
        this.setupListenerCallbacks();
        this.setupIframe();
    }

    /**
     * On component destroy, call super.$onDestroy() to unregister event handlers
     */
    $onDestroy() {
        this.disposeEventListeners();
    }

    isNetworkAllowed(networkName) {
        return !this.allowedNetworks || !this.allowedNetworks.length || find(this.allowedNetworks, network => network === networkName);
    }

    /**
     * Setups the iframe and starts a timer to ensure it loads
     */
    setupIframe() {
        this.isLoading = true;
        this.isIframeLoadedSuccess = false;
        this.iframeUrl = undefined;
        this.digitalPayPaymentInstrumentsService.initializeCreditCardIframe().then((response) => {
            if (!response.Success) {
                this.payFrameFailLoad();
                this.setupIframeLoadTimer();
                return;
            }
            this.iframeUrl = this.$sce.trustAsResourceUrl(response.IframeUrl);
            this.registerEventListeners();

            this.setupIframeLoadTimer();
        }).catch(() => {
            this.isLoading = false;
        });
    }

    /**
     * Setup a timer to wait for the iframe to load
     */
    setupIframeLoadTimer() {
        this.iframeLoadTimeoutPromise = this.$timeout(() => {
            this.isLoading = false;
        }, this.timeoutPeriodInMillseconds);
    }

    setupListenerCallbacks() {
        this.payFrameErrorCallback = (response) => {
            this.payFrameError(response);
        };

        this.payFrameSuccessCallback = (response) => {
            this.payFrameSuccess(response);
        };

        this.payFrameLoadedCallback = () => {
            this.payFrameLoaded();
        };

        this.payFrameValidCallback = () => {
            this.payFrameValid();
        };

        this.payFrameInvalidCallback = () => {
            this.payFrameInvalid();
        };

        this.payFrameFocusGainCallback = (response) => {
            this.payFrameFocusGain(response);
        };

        this.payFrameFocusLostCallback = (response) => {
            this.payFrameFocusLost(response);
        };

        this.payFrameEnterKeyPressedCallback = () => {
            this.payFrameEnterKeyPressed();
        };
    }

    registerEventListeners() {
        this.iframeEventHandler.on('pay-frame-error', this.payFrameErrorCallback);
        this.iframeEventHandler.on('pay-frame-success', this.payFrameSuccessCallback);
        this.iframeEventHandler.on('pay-frame-loaded', this.payFrameLoadedCallback);
        this.iframeEventHandler.on('pay-frame-valid', this.payFrameValidCallback);
        this.iframeEventHandler.on('pay-frame-invalid', this.payFrameInvalidCallback);
        this.iframeEventHandler.on('pay-frame-focus-gain', this.payFrameFocusGainCallback);
        this.iframeEventHandler.on('pay-frame-focus-lost', this.payFrameFocusLostCallback);
        this.iframeEventHandler.on('pay-frame-enterKey-pressed', this.payFrameEnterKeyPressedCallback);
    }

    disposeEventListeners() {
        this.iframeEventHandler.off('pay-frame-error', this.payFrameErrorCallback);
        this.iframeEventHandler.off('pay-frame-success', this.payFrameSuccessCallback);
        this.iframeEventHandler.off('pay-frame-loaded', this.payFrameLoadedCallback);
        this.iframeEventHandler.off('pay-frame-valid', this.payFrameValidCallback);
        this.iframeEventHandler.off('pay-frame-invalid', this.payFrameInvalidCallback);
        this.iframeEventHandler.off('pay-frame-focus-gain', this.payFrameFocusGainCallback);
        this.iframeEventHandler.off('pay-frame-focus-lost', this.payFrameFocusLostCallback);
        this.iframeEventHandler.off('pay-frame-enterKey-pressed', this.payFrameEnterKeyPressedCallback);
    }

    /**
     * Event when the iframe fails to load from Trader side
     */
    payFrameFailLoad() {
    }

    /**
     * Event when the iframe first loads
     */
    payFrameLoaded() {
        this.$timeout(() => {
            // Cancel the timer and let the view know the iframe was successfully loaded
            this.$timeout.cancel(this.iframeLoadTimeoutPromise);
            if (this.isLoading) {
                this.isLoading = false;
                this.isIframeLoadedSuccess = true;
            }
        });
    }

    /**
     * Event when the iframe does NOT successfully submit
     * @param {Object} response { eventId: 'pay-frame-error', paymentInstrumentId: (string), message: (string), code: (string), auditId: (string) }
     */
    // eslint-disable-next-line no-unused-vars
    payFrameError(response) {
    }

    /**
     * Event when the iframe successfully submits
     * @param {Object} response { eventId: 'pay-frame-success', paymentInstrumentId: (string), message: (string), code: (string), auditId: (string), stepUpToken: (string) }
     */
    // eslint-disable-next-line no-unused-vars
    payFrameSuccess(response) {
    }

    /**
     * Event when the iframe goes from invalid to invalid
     * Note that this only fires when the form validation state changes (e.g. NOT valid -> valid)
     */
    payFrameValid() {
    }

    /**
     * Event when the iframe goes from valid to invalid
     * Note that this only fires when the form validation state changes (e.g. NOT invalid -> invalid)
     */
    payFrameInvalid() {
    }

    /**
     * Event when an input in the iframe obtains focus
     */
    payFrameFocusGain() {
    }

    /**
     * Event when an input in the iframe loses focus
     */
    payFrameFocusLost() {
    }

    /**
     * Event when enter is pressed in the iframe (typically used for submit actions)
     */
    payFrameEnterKeyPressed() {
    }

    /**
     * Signal the iframe to clear the contents of the form
     */
    clearIframeContents() {
        this.isSavingCard = this.defaultIsSavingCard;
        this.iframeEventHandler.emit(this.$element.find(`#${IFRAMEID}`)[0], { eventId: 'pay-frame-clear' });
        this.clearValidationState();
    }

    /**
     * Signal the iframe to submit the contents of the form
     */
    submitIframe() {
        this.iframeEventHandler.emit(this.$element.find(`#${IFRAMEID}`)[0], { eventId: 'pay-frame-submit', save: this.isSavingCard, verify: this.requiresVerification });
    }
}

const creditCardAddBaseComponentDefinition = {
    controller: CreditCardAddBaseController,
};

module.exports = {
    CreditCardAddBaseController,
    creditCardAddBaseComponentDefinition,
};



// WEBPACK FOOTER //
// ./src/wallet/creditcard/credit-card-add-base.component.js